<script lang="ts">
  import { client_config } from "../stores/store";
  import { regexForHousinghand } from "../utils/validators";
  import WhatsappLogo from "../utils/assets/whatsapp_logo.svelte";
  import PhoneSolidIcon from "../utils/assets/phone_solid_icon.svelte";
  import WechatSolidIcon from "../utils/assets/wechat_solid_icon.svelte";
  import WechatIcon from "../utils/assets/wechat_icon.svelte";
  import CrossIcon from "../utils/assets/cross_icon.svelte";
  import FooterLogo from "../utils/assets/footer_logo.svelte";
  import { trackClick } from "../utils/clickTracker";
  import { client_id } from "../stores/chat";
  import {
    WIDGET_BOTTOM_SPACE,
    COMMUNICATION_REDIRECTS_CUSTOM_BOTTOM_SPACE,
  } from "../utils/constants";
  import { resolveButtonSizes, commSizeVars } from "../utils/buttonSizing";

  $: buttonSizes = resolveButtonSizes($client_config);

  type RedirectIcon = {
    type: "call" | "whatsapp" | "wechat";
    side: "left" | "right";
    text: string;
    textColor: string;
    bgColor: string;
  };

  export let id: string;
  export let verticalOffset: string = "16px";

  let isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  let wechatPopupOpen = false;
  let wechatImgError = false;

  const handleCommunicationClick = async (type: "call" | "whatsapp") => {
    if (type === "call") {
      window.location.href = `tel:${$client_config?.call_link?.replace(/[^\d+]/g, "")}`;
    } else if (type === "whatsapp") {
      window.open($client_config?.whatsapp_link, "_blank");
    }

    try {
      await trackClick(id, type, "homepage");
    } catch (error) {
      console.error("Failed to track click:", error);
    }
  };

  const is_housinghand = id === "housinghand";

  $: call_bg_color =
    $client_config?.communication_redirects_config?.call?.background_colour ||
    $client_config?.communication_redirects_background_colour || "#f67e35";

  $: whatsapp_bg_color =
    $client_config?.communication_redirects_config?.whatsapp?.background_colour ||
    $client_config?.communication_redirects_background_colour || "#f67e35";

  $: call_text_color =
    $client_config?.communication_redirects_config?.call?.text_colour ||
    "#FFFFFF";

  $: call_text =
    $client_config?.communication_redirects_config?.call?.text || "Call Us!";

  $: whatsapp_text_color =
    $client_config?.communication_redirects_config?.whatsapp?.text_colour ||
    "#FFFFFF";

  $: whatsapp_text =
    $client_config?.communication_redirects_config?.whatsapp?.text ||
    "Let's chat";

  $: is_call_enabled = is_housinghand
    ? !regexForHousinghand?.test(window?.location?.href) &&
      $client_config?.communication_redirects_config?.call?.enabled &&
      $client_config?.call_link
    : $client_config?.communication_redirects_config?.call?.enabled &&
      $client_config?.call_link;

  $: is_whatsapp_enabled = is_housinghand
    ? !regexForHousinghand?.test(window?.location?.href) &&
      $client_config?.communication_redirects_config?.whatsapp?.enabled &&
      $client_config?.whatsapp_link
    : $client_config?.communication_redirects_config?.whatsapp?.enabled &&
      $client_config?.whatsapp_link;

  $: wechat_bg_color =
    $client_config?.communication_redirects_config?.wechat?.background_colour ||
    $client_config?.communication_redirects_background_colour || "#f67e35";

  $: wechat_text_color =
    $client_config?.communication_redirects_config?.wechat?.text_colour ||
    "#FFFFFF";

  $: wechat_text =
    $client_config?.communication_redirects_config?.wechat?.text ||
    "Let's chat";

  // Header copy for the QR card. The redirect `text` is the floating-button
  // hover label, so it isn't reused here; matches the "Scan to continue" design.
  const wechatPopupTitle = "Scan to continue on WeChat";

  $: is_wechat_enabled = is_housinghand
    ? !regexForHousinghand?.test(window?.location?.href) &&
      $client_config?.communication_redirects_config?.wechat?.enabled &&
      $client_config?.wechat_link
    : $client_config?.communication_redirects_config?.wechat?.enabled &&
      $client_config?.wechat_link;

  $: launcherSide = (
    $client_config?.widget_positioning?.position === "bottom_left"
      ? "left"
      : "right"
  ) as "left" | "right";
  $: callIconSide = ($client_config?.communication_redirects_config?.call
    ?.redirect_position || launcherSide) as "left" | "right";
  $: whatsappIconSide = ($client_config?.communication_redirects_config
    ?.whatsapp?.redirect_position || launcherSide) as "left" | "right";
  $: wechatIconSide = ($client_config?.communication_redirects_config?.wechat
    ?.redirect_position || launcherSide) as "left" | "right";

  $: mobileBottomPx = parseInt(
    WIDGET_BOTTOM_SPACE[$client_id as keyof typeof WIDGET_BOTTOM_SPACE]
      ?.mobile || "16", 10
  );
  $: desktopBottomPx = parseInt(verticalOffset, 10) || 16;
  $: effectiveBottomPx = isMobile ? mobileBottomPx : desktopBottomPx;
  // Keep the gap between the launcher and a same-side redirect button fixed
  // regardless of the (configurable) launcher size. 16px matches the previous
  // spacing when the launcher was a fixed 80px (80 + 16 = 96).
  $: launcherHeightPx =
    parseInt(
      (typeof window !== "undefined" && window.innerWidth <= 430
        ? buttonSizes.launcher.mobile
        : buttonSizes.launcher.desktop) || "80",
      10,
    ) || 80;
  $: sameSideBottom = effectiveBottomPx + launcherHeightPx + 16 + "px";

  $: customBottomSpace =
    COMMUNICATION_REDIRECTS_CUSTOM_BOTTOM_SPACE?.[
      id as keyof typeof COMMUNICATION_REDIRECTS_CUSTOM_BOTTOM_SPACE
    ];
  $: oppositeSideBottom = customBottomSpace
    ? (isMobile ? customBottomSpace.mobile : customBottomSpace.desktop) || effectiveBottomPx + "px"
    : effectiveBottomPx + "px";

  $: redirectIcons = [
    is_wechat_enabled && {
      type: "wechat",
      side: wechatIconSide,
      text: wechat_text,
      textColor: wechat_text_color,
      bgColor: wechat_bg_color,
    },
    is_call_enabled && {
      type: "call",
      side: callIconSide,
      text: call_text,
      textColor: call_text_color,
      bgColor: call_bg_color,
    },
    is_whatsapp_enabled && {
      type: "whatsapp",
      side: whatsappIconSide,
      text: whatsapp_text,
      textColor: whatsapp_text_color,
      bgColor: whatsapp_bg_color,
    },
  ].filter((icon): icon is RedirectIcon => Boolean(icon));

  const handleRedirectClick = (icon: RedirectIcon) => {
    if (icon.type === "wechat") {
      wechatPopupOpen = !wechatPopupOpen;
      if (wechatPopupOpen) {
        wechatImgError = false;
        trackClick(id, "wechat", "homepage");
      }
      return;
    }
    handleCommunicationClick(icon.type);
  };
</script>

{#each ["left", "right"] as side}
  {#if redirectIcons.find((icon) => icon?.side === side)}
    <div
      style="bottom: {launcherSide ===
      side
        ? sameSideBottom
        : oppositeSideBottom} !important;"
      class="verbaflo-widget-communication-btn-container {side}"
      class:same-side-as-launcher={launcherSide === side}
    >
      {#each redirectIcons.filter((icon) => icon.side === side) as icon}
        {#if wechatPopupOpen && icon.type === "wechat"}
          <div
            class="verbaflo-wechat-popup"
            role="dialog"
            aria-label="WeChat QR code"
          >
            <div class="verbaflo-wechat-popup-header">
              <div class="verbaflo-wechat-popup-title">
                <WechatIcon size="20" />
                <span>{wechatPopupTitle}</span>
              </div>
              <button
                type="button"
                class="verbaflo-wechat-popup-close"
                aria-label="Close"
                on:click={() => (wechatPopupOpen = false)}
              >
                <CrossIcon color="#ffffff" size="16" />
              </button>
            </div>
            {#if $client_config?.wechat_link && !wechatImgError}
              <img
                src={$client_config?.wechat_link}
                alt="WeChat QR code"
                class="verbaflo-wechat-popup-qr"
                on:error={() => (wechatImgError = true)}
              />
            {:else}
              <div class="verbaflo-wechat-popup-fallback">
                Couldn't load QR code. Please try again later.
              </div>
            {/if}
            <div class="verbaflo-wechat-popup-footer">
              <span class="verbaflo-wechat-popup-footer-text">Powered by</span>
              <FooterLogo />
              <a
                href="https://verbaflo.ai"
                target="_blank"
                rel="noopener noreferrer"
                class="verbaflo-wechat-popup-footer-link"
              >
                VerbaFlo.
              </a>
            </div>
          </div>
        {/if}
        <button
          type="button"
          class="verbaflo-widget-communication-btn"
          style="--communication_redirects_bg_color: {icon.bgColor}; --communication_redirects_text_color: {icon.textColor}; {commSizeVars(
            icon.type,
            buttonSizes[icon.type],
          )}"
          aria-label={icon.text}
          on:click={() => handleRedirectClick(icon)}
        >
          {#if icon.type === "call"}
            <PhoneSolidIcon color={icon.textColor} />
          {:else if icon.type === "wechat"}
            <WechatSolidIcon color={icon.textColor} />
          {:else}
            <WhatsappLogo color={icon.textColor} />
          {/if}
          <span class="verbaflo-widget-communication-text">{icon.text}</span>
        </button>
      {/each}
    </div>
  {/if}
{/each}

<style>
  :global(#vfwidget .verbaflo-widget-communication-btn-container) {
    position: fixed !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn-container.left) {
    left: 16px !important;
    align-items: flex-start !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn-container.right) {
    right: 16px !important;
    align-items: flex-end !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-communication-btn-container.same-side-as-launcher.left
    ) {
    left: 26px !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-communication-btn-container.same-side-as-launcher.right
    ) {
    right: 26px !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn) {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    width: var(--vf-comm-size-d, 60px) !important;
    height: var(--vf-comm-size-d, 60px) !important;
    min-height: var(--vf-comm-size-d, 60px) !important;
    padding: 0 14px !important;
    margin: 0 !important;
    border: 1px solid rgba(0, 0, 0, 0.05) !important;
    border-radius: 50px !important;
    background-color: var(--communication_redirects_bg_color) !important;
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.1),
      0 1px 3px rgba(0, 0, 0, 0.08) !important;
    overflow: hidden !important;
    transition:
      width 0.3s ease-in-out,
      box-shadow 0.2s ease-in-out !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn:active) {
    background-color: var(--communication_redirects_bg_color) !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn:hover) {
    background-color: var(--communication_redirects_bg_color) !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-btn svg) {
    width: calc(
      var(--vf-comm-size-d, 60px) * var(--vf-comm-icon-ratio, 0.5)
    ) !important;
    height: calc(
      var(--vf-comm-size-d, 60px) * var(--vf-comm-icon-ratio, 0.5)
    ) !important;
    min-width: calc(
      var(--vf-comm-size-d, 60px) * var(--vf-comm-icon-ratio, 0.5)
    ) !important;
    max-width: 100% !important;
    max-height: 100% !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-communication-text) {
    font-family: "Unbounded", sans-serif !important;
    width: 0 !important;
    opacity: 0 !important;
    white-space: nowrap !important;
    margin-left: 0 !important;
    transition: all 0.3s ease-in-out !important;
    color: var(--communication_redirects_text_color) !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    overflow: hidden !important;
  }

  @media only screen and (min-width: 1024px) {
    :global(#vfwidget .verbaflo-widget-communication-btn:hover) {
      width: auto !important;
      justify-content: flex-start !important;
    }

    :global(
        #vfwidget
          .verbaflo-widget-communication-btn:hover
          .verbaflo-widget-communication-text
      ) {
      width: auto !important;
      opacity: 1 !important;
      margin-left: 8px !important;
    }
  }

  :global(#vfwidget .verbaflo-wechat-popup) {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 16px !important;
    width: 300px !important;
    max-width: calc(100vw - 32px) !important;
    box-sizing: border-box !important;
    padding: 16px !important;
    background-color: #ffffff !important;
    border: 1px solid #e9eaeb !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16) !important;
    z-index: 2147483000 !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-header) {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 8px !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-title) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    min-width: 0 !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-title span) {
    color: #252b37 !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 20px !important;
    text-transform: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-close) {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 50% !important;
    background-color: #252b37 !important;
    cursor: pointer !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-close:hover) {
    opacity: 0.85 !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-qr) {
    width: 220px !important;
    max-width: 100% !important;
    height: 220px !important;
    aspect-ratio: 1 / 1 !important;
    object-fit: contain !important;
    display: block !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-fallback) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    width: 220px !important;
    max-width: 100% !important;
    min-height: 220px !important;
    box-sizing: border-box !important;
    padding: 16px !important;
    color: #717680 !important;
    font-size: 13px !important;
    line-height: 18px !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-footer) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 4px !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-footer-text) {
    color: #737373 !important;
    font-size: 10px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-wechat-popup-footer-link) {
    text-decoration: none !important;
    color: #051133 !important;
    font-weight: bold !important;
    font-size: 12px !important;
    cursor: pointer !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  @media only screen and (max-width: 430px) {
    :global(#vfwidget .verbaflo-widget-communication-btn) {
      width: var(--vf-comm-size-m, 50px) !important;
      height: var(--vf-comm-size-m, 50px) !important;
      min-height: var(--vf-comm-size-m, 50px) !important;
    }

    :global(#vfwidget .verbaflo-widget-communication-btn-container) {
      gap: 12px !important;
    }

    :global(#vfwidget .verbaflo-widget-communication-btn svg) {
      width: calc(
        var(--vf-comm-size-m, 50px) * var(--vf-comm-icon-ratio, 0.5)
      ) !important;
      height: calc(
        var(--vf-comm-size-m, 50px) * var(--vf-comm-icon-ratio, 0.5)
      ) !important;
      min-width: calc(
        var(--vf-comm-size-m, 50px) * var(--vf-comm-icon-ratio, 0.5)
      ) !important;
      flex-shrink: 0 !important;
    }
  }
</style>
